import React, { useEffect, useState, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getFuelReportDetailed } from '../../services/api';
import { FuelReportDetailed } from '../../types';
import { parseUtc } from '../../utils/dataAge';
import './FuelReport.css';

interface Props {
  deviceId: number;
  dateFrom: string;
  dateTo: string;
  deviceName?: string;
}

const FuelDot = (props: any) => {
  const { cx, cy, payload } = props;
  if (!payload?.event_type) return null;
  const isRefuel = payload.event_type === 'refuel';
  return (
    <g>
      <circle cx={cx} cy={cy} r={7} fill={isRefuel ? '#22c55e' : '#ef4444'} />
      <text
        x={cx} y={cy - 14}
        textAnchor="middle"
        fill={isRefuel ? '#22c55e' : '#ef4444'}
        fontSize={15}
        fontWeight="bold"
      >
        {isRefuel ? '↑' : '↓'}
      </text>
    </g>
  );
};

const FuelReport: React.FC<Props> = ({ deviceId, dateFrom, dateTo, deviceName }) => {
  const [data, setData] = useState<FuelReportDetailed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    getFuelReportDetailed(deviceId, dateFrom, dateTo)
      .then((res: FuelReportDetailed) => {
        if (!cancelled) {
          if (res.error === 'no_fuel_sensor') {
            setError('Brak danych czujnika paliwa dla tego pojazdu w wybranym zakresie.');
          } else {
            setData(res);
          }
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          const detail = err?.response?.data;
          if (detail?.error === 'no_fuel_sensor') {
            setError('Brak danych czujnika paliwa dla tego pojazdu w wybranym zakresie.');
          } else {
            setError(detail?.detail || 'Nie udało się pobrać raportu paliwa.');
          }
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [deviceId, dateFrom, dateTo]);

  const handleExportPdf = async () => {
    if (!pdfRef.current || !data) return;
    setExporting(true);
    try {
      const el = pdfRef.current;
      const canvas = await html2canvas(el, {
        scale: 1.5,
        backgroundColor: '#1a1d23',
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pageW = canvas.width / 1.5;
      const pageH = canvas.height / 1.5;
      const pdf = new jsPDF({
        orientation: pageW > pageH ? 'landscape' : 'portrait',
        unit: 'px',
        format: [pageW, pageH],
      });
      pdf.addImage(imgData, 'JPEG', 0, 0, pageW, pageH);
      pdf.save(`raport-paliwa_${deviceName || deviceId}_${dateFrom}_${dateTo}.pdf`);
    } catch {
      // silent
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="fuel-report-loading">Wczytywanie raportu paliwa…</div>;
  }

  if (error) {
    return <div className="fuel-report-error">{error}</div>;
  }

  if (!data || !data.summary) {
    return <div className="fuel-report-empty">Brak danych dla wybranego zakresu.</div>;
  }

  const { summary, daily, fuel_events, chart_data } = data;

  const fmtL = (v: number | null) => v !== null && v !== undefined ? `${v} L` : '—';
  const fmtKm = (v: number) => `${v} km`;
  const fmtAvg = (v: number | null) => v !== null && v !== undefined ? `${v} L/100km` : '—';

  const fmtTime = (iso: string) => {
    const d = parseUtc(iso);
    return d.toLocaleString('pl', {
      month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const fmtDate = (iso: string) => {
    const d = parseUtc(iso);
    return d.toLocaleDateString('pl', { weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const tickFmt = (v: string) => {
    const d = parseUtc(v);
    return d.toLocaleTimeString('pl', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fuel-report">
      <div className="fuel-report-actions">
        <button
          className="btn-export-pdf"
          onClick={handleExportPdf}
          disabled={exporting}
        >
          {exporting ? 'Generowanie…' : 'Pobierz PDF'}
        </button>
      </div>

      {/* PDF-capturable content */}
      <div ref={pdfRef} id="fuel-pdf-content" className="fuel-pdf-content">
        {/* PDF header (visible only in PDF context) */}
        <div className="fuel-pdf-header">
          <img src="/logo.svg" alt="10drive" className="fuel-pdf-logo" />
          <div className="fuel-pdf-title">
            <span className="fuel-pdf-vehicle">{deviceName || `Pojazd #${deviceId}`}</span>
            <span className="fuel-pdf-range">{dateFrom} – {dateTo}</span>
          </div>
        </div>

        {/* Summary cards */}
        <div className="fuel-summary-cards">
          <div className="fuel-card">
            <div className="fuel-card-label">Paliwo na początku</div>
            <div className="fuel-card-value">{fmtL(summary.fuel_start)}</div>
          </div>
          <div className="fuel-card">
            <div className="fuel-card-label">Paliwo na końcu</div>
            <div className="fuel-card-value">{fmtL(summary.fuel_end)}</div>
          </div>
          <div className="fuel-card fuel-card-highlight">
            <div className="fuel-card-label">Łączne zużycie</div>
            <div className="fuel-card-value">{fmtL(summary.fuel_consumed)}</div>
          </div>
          <div className="fuel-card">
            <div className="fuel-card-label">Dystans</div>
            <div className="fuel-card-value">{fmtKm(summary.distance_km)}</div>
          </div>
          <div className="fuel-card">
            <div className="fuel-card-label">Śr. spalanie</div>
            <div className="fuel-card-value">{fmtAvg(summary.avg_consumption)}</div>
          </div>
          <div className="fuel-card fuel-card-green">
            <div className="fuel-card-label">Tankowania ↑</div>
            <div className="fuel-card-value">{summary.refuels_count}</div>
          </div>
          <div className="fuel-card fuel-card-red">
            <div className="fuel-card-label">Ubytki ↓</div>
            <div className="fuel-card-value">{summary.drains_count}</div>
          </div>
        </div>

        {/* Chart */}
        {chart_data.length > 0 && (
          <div className="fuel-chart-section">
            <h3 className="fuel-section-title">Poziom paliwa</h3>
            <div className="fuel-chart-container">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chart_data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3a3f4a" />
                  <XAxis
                    dataKey="time"
                    tick={{ fill: '#9aa0ad', fontSize: 11 }}
                    tickFormatter={tickFmt}
                    interval="preserveStartEnd"
                    minTickGap={60}
                  />
                  <YAxis
                    tick={{ fill: '#9aa0ad', fontSize: 11 }}
                    tickFormatter={(v) => `${v}L`}
                    domain={['auto', 'auto']}
                    width={45}
                  />
                  <Tooltip
                    contentStyle={{ background: '#22262e', border: '1px solid #3a3f4a', color: '#e8eaed', fontSize: 12 }}
                    formatter={(v: any) => [`${v} L`, 'Paliwo (mediana)']}
                    labelFormatter={(l) => parseUtc(l).toLocaleString('pl')}
                  />
                  <Line
                    type="monotone"
                    dataKey="fuel_median"
                    stroke="#4a6fa5"
                    strokeWidth={2}
                    dot={<FuelDot />}
                    activeDot={{ r: 4, fill: '#4a6fa5' }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="fuel-chart-legend">
              <span className="legend-refuel">↑ tankowanie</span>
              <span className="legend-drain">↓ ubytek</span>
            </div>
          </div>
        )}

        {/* Daily table */}
        {daily.length > 0 && (
          <div className="fuel-table-section">
            <h3 className="fuel-section-title">Zestawienie dzienne</h3>
            <div className="fuel-table-wrapper">
              <table className="fuel-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Poziom (start)</th>
                    <th>Poziom (koniec)</th>
                    <th>Zużycie</th>
                    <th>Dystans</th>
                    <th>Śr. spalanie</th>
                  </tr>
                </thead>
                <tbody>
                  {daily.map((row) => (
                    <tr key={row.date}>
                      <td>{fmtDate(row.date)}</td>
                      <td>{fmtL(row.fuel_start)}</td>
                      <td>{fmtL(row.fuel_end)}</td>
                      <td>{fmtL(row.fuel_consumed)}</td>
                      <td>{fmtKm(row.distance_km)}</td>
                      <td>{fmtAvg(row.avg_consumption)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Events table */}
        {fuel_events.length > 0 && (
          <div className="fuel-table-section">
            <h3 className="fuel-section-title">Zdarzenia paliwowe</h3>
            <div className="fuel-table-wrapper">
              <table className="fuel-table">
                <thead>
                  <tr>
                    <th>Typ</th>
                    <th>Czas</th>
                    <th>Przed</th>
                    <th>Po</th>
                    <th>Zmiana</th>
                  </tr>
                </thead>
                <tbody>
                  {fuel_events.map((ev, i) => (
                    <tr key={i} className={ev.event_type === 'refuel' ? 'row-refuel' : 'row-drain'}>
                      <td>
                        {ev.event_type === 'refuel'
                          ? <span className="badge-refuel">↑ Tankowanie</span>
                          : <span className="badge-drain">↓ Ubytek</span>}
                      </td>
                      <td>{fmtTime(ev.event_time)}</td>
                      <td>{fmtL(ev.fuel_before)}</td>
                      <td>{fmtL(ev.fuel_after)}</td>
                      <td className={ev.event_type === 'refuel' ? 'delta-positive' : 'delta-negative'}>
                        {ev.event_type === 'refuel' ? '+' : ''}{ev.fuel_delta} L
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelReport;
